import React, { useContext, createContext, useState, useEffect } from 'react';
import { AES, enc } from 'crypto-js';
import { toast } from 'react-toastify';
import useIndexedDB from '@/hooks/useIndexedDB';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';

const OrdersContext = createContext();

const secretKey = import.meta.env.VITE_AES_ENCRYPTION_KEY;
const getCurrentTime = () => new Date().getTime();
const EXPIRATION_TIME = 3600 * 1000;
const isExpired = (timestamp) => {
	return getCurrentTime() - Number(timestamp) > EXPIRATION_TIME;
};

// Function to generate a random secret key
const generateSecretKey = (length = 32) => {
	// Generate random bytes
	const randomValues = new Uint8Array(length);
	window.crypto.getRandomValues(randomValues);

	// Convert the random bytes to a hex string
	const key = Array.from(randomValues, (byte) => ('0' + byte.toString(16)).slice(-2)).join('');
	return key;
};

const initialCart = {
	currentStepNb: 0,
	modalStepIndex: 0,
	restaurant_id: '',
	delivery_address: '',
	delivery_date: '',
	delivery_firstname: '',
	delivery_lastname: '',
	delivery_phone: '',
	delivery_message: '',
	delivery_fee_applied: '',
	billing_firstname: '',
	billing_lastname: '',
	billing_phone: '',
	billing_address: '',
	billing_company: '',
	billing_email: '',
	fetched_menus: [],
	menus: [],
	categories: [],
	showModal: false,
	is_no_limit: false,
	is_no_limit_type: '',
	is_no_limit_minimum_amount: '',
	is_no_limit_selected_category: {},
};

const OrdersProvider = ({ children }) => {
	const [restaurantId, setRestaurantId] = useState(null);
	const [cart, setCart] = useState(initialCart);
	const [promoId, setpromoId] = useState('');
	const [promoApplied, setPromoApplied] = useState(null);
	const [isWalletModalOpen, setIsWalletModalOpen] = useState(false);
	const { saveCart, loadCart, isInitialized, deleteAllDBs } = useIndexedDB();
	const navigate = useNavigate();
	const location = useLocation();

	const getDefaultRestaurantId = async () => {
		try {
			const response = await axios.get(`${import.meta.env.VITE_API_URL}/api/restaurants/get-first-priority-restaurant`);

			if (response.status === 200) {
				saveHashedRestaurantId(response.data.restaurant_id);
				setCart((prevCart) => ({ ...prevCart, is_no_limit: response.data.is_no_limit, is_no_limit_type: response.data.is_no_limit_type, is_no_limit_minimum_amount: response.data.is_no_limit_minimum_amount, is_no_limit_selected_category: response.data.is_no_limit_selected_category }));

				return response.data.restaurant_id;
			}
		} catch (error) {
			console.info(error);

			toast.error('Une erreur est survenue', {
				position: 'top-center',
				autoClose: 1000,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: false,
				draggable: true,
				theme: 'colored',
			});
		}
	};

	// Encrypt and save the restaurant ID
	const saveHashedRestaurantId = (id) => {
		try {
			const hashedId = AES.encrypt(id.toString(), secretKey).toString();
			localStorage.setItem('restaurant_id_ref', hashedId);
		} catch (error) {
			console.error('Encryption failed:', error);
		}
	};

	// Retrieve and compare the restaurant ID
	const getRestaurantId = async () => {
		const storedHashedId = localStorage.getItem("restaurant_id_ref");
		if (!storedHashedId) {
			return await getDefaultRestaurantId(); // or handle the case where there's no stored data
		}

		try {
			// Decrypt stored ID
			const bytes = AES.decrypt(storedHashedId, secretKey);
			const originalStoredId = bytes.toString(enc.Utf8);

			return originalStoredId;
		} catch (error) {
			console.error('Decryption failed:', error);
		}

		return null;
	};

	const handleApplyPromo = async () => {
		try {
			const response = await axios.post(
				// Mise à jour de l'URL pour inclure restaurantId et promoId dans l'URL
				`${import.meta.env.VITE_API_URL}/api/restaurants/${cart.restaurant_id}/promos/${promoId.toUpperCase()}/verify`, // API de vérification du code promo
				{
					totalPrice: handleGetTotalPrice().total, // Envoyer le prix total dans le corps de la requête
					userEmail: cart.billing_email || '',
					userPhone: cart.delivery_phone || '',
				}
			);

			if (response.data.valid) {
				setPromoApplied(response.data.promo);
				toast.success(`Code promo appliqué : ${response.data.promo.reference}`, {
					position: 'top-center',
					autoClose: 800,
					hideProgressBar: false,
					closeOnClick: true,
					pauseOnHover: false,
					draggable: true,
					theme: 'colored',
				});
			}
		} catch (error) {
			// Vérifier si c'est une erreur "déjà filleul"
			if (error.response?.data?.isAlreadyReferred || 
			    (error.response?.data?.message && error.response.data.message.includes('déjà filleul'))) {
				// Vider le champ promo
				setpromoId('');
				setPromoApplied(null);
				
				// Toast d'erreur explicite
				toast.error('Vous êtes déjà filleul d\'un autre parrain. Vous ne pouvez pas utiliser un autre code de parrainage.', {
					position: 'top-center',
					autoClose: 5000,
					hideProgressBar: false,
					closeOnClick: true,
					pauseOnHover: true,
					draggable: true,
					theme: 'colored',
				});
				return;
			}

			if (error.response && error.response.data.message) {
				toast.error(error.response.data.message, {
					position: 'top-center',
					autoClose: 800,
					hideProgressBar: false,
					closeOnClick: true,
					pauseOnHover: false,
					draggable: true,
					theme: 'colored',
				}); // Affiche un message d'erreur
			} else {
				toast.error('Erreur lors de la vérification du code promo', {
					position: 'top-center',
					autoClose: 800,
					hideProgressBar: false,
					closeOnClick: true,
					pauseOnHover: false,
					draggable: true,
					theme: 'colored',
				});
			}
			setPromoApplied(null); // Remet à zéro si la promo est invalide
		}
	};

	const handleCancelPromo = () => {
		setPromoApplied(null);
		setpromoId('');
		handleGetTotalPrice();
	};

	const handleGetTotalPrice = () => {
		const totalMenuPrice =
			cart?.menus?.reduce((totalPrice, menu) => totalPrice + menu?.price * menu?.menu_quantity, 0) ?? 0;

		const totalCategoriesPrice =
			cart?.categories?.reduce((totalPrice, category) => {
				const extraProductsPrice =
					category?.extra_products?.reduce(
						(stepTotalPrice, product) => stepTotalPrice + product?.price * product?.selected_quantity,
						0
					) ?? 0;

				return totalPrice + extraProductsPrice;
			}, 0) ?? 0;

		const baseTotal = parseFloat(totalMenuPrice + totalCategoriesPrice);
		const formattedTotal = Number(baseTotal.toFixed(2));

		// No promo applied: return base total
		if (!promoApplied) {
			return {
				total: formattedTotal,
				totalWithPromo: formattedTotal,
				isFreeOrder: false
			};
		}

		// Referral code: no immediate discount, rewards credited after payment
		if (promoApplied.isReferralCode) {
			return {
				total: formattedTotal,
				totalWithPromo: formattedTotal,
				isFreeOrder: false
			};
		}

		// Free order promo
		if (promoApplied.discountType === 'free') {
			return {
				total: formattedTotal,
				totalWithPromo: 0,
				isFreeOrder: true
			};
		}

		// Percentage discount
		if (promoApplied.discountType === 'percentage') {
			const discountAmount = (formattedTotal * promoApplied.discountValue) / 100;
			const totalWithPromo = Number((formattedTotal - discountAmount).toFixed(2));
			return {
				total: formattedTotal,
				totalWithPromo: Math.max(0, totalWithPromo),
				isFreeOrder: false
			};
		}

		// Fixed discount
		if (promoApplied.discountType === 'fixed') {
			const totalWithPromo = Number((formattedTotal - promoApplied.discountValue).toFixed(2));
			return {
				total: formattedTotal,
				totalWithPromo: Math.max(0, totalWithPromo),
				isFreeOrder: false
			};
		}

		// Default: no discount applied
		return {
			total: formattedTotal,
			totalWithPromo: formattedTotal,
			isFreeOrder: false
		};
	};

	const fetchInitialCategories = async (noLimitSelectedCategory = null, restaurantIdParam = null) => {
		try {
			console.log(restaurantIdParam, restaurantId);
			const response = await axios.get(`${import.meta.env.VITE_API_URL}/api/settings/categories/list`, {
				params: {
					restaurantId: restaurantIdParam || restaurantId
				}
			});

			if (response.status === 200) {
				setCart((prevState) => ({
					...prevState,
					categories: response.data.map((category) => {
						return {
							...category,
							is_additional: false,
							step_quantity: 0,
							menu_quantity: 0,
							selected_products: [],
							extra_products: [],
							is_done: false,
							showModal: false,
						};
					}),
				}));
			}
		} catch (error) {
			console.error('Error fetching initial categories:', error);

			toast.error("Nous n'avons pas pu récupérer les catégories", {
				position: 'top-center',
				autoClose: 500,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: false,
				draggable: true,
				theme: 'colored',
			});
		}
	};

	const getIDBCart = async (restaurantId) => {
		if (!isInitialized) {
			return null;
		}

		try {
			const cartFromIndexedDB = await loadCart(restaurantId);
			return cartFromIndexedDB;
		} catch (error) {
			console.error('Error loading cart:', error);
			return null;
		}
	};

	useEffect(() => {
		const initializeCart = async () => {
			if (!isInitialized) return;

			try {
				const restaurantId = await getRestaurantId();
				if (!restaurantId) return;

				const savedCart = await loadCart(restaurantId);
				if (savedCart) {
					setCart(prevCart => ({
						...prevCart,
						...savedCart,
						restaurant_id: restaurantId
					}));
				}
			} catch (error) {
				console.error('Error loading cart:', error);
			}
		};

		if (location.pathname === '/commande/adresse') {
			deleteAllDBs();
		}

		initializeCart();
	}, [isInitialized]); // Only run when IndexedDB is initialized

	useEffect(() => {
		if (location.pathname === '/commande/adresse') {
			localStorage.removeItem('restaurant_id_ref');
			setCart(initialCart);
			setRestaurantId(null);
		}

		const defineDefaultRestaurantId = async () => {
			const restaurantId = await getRestaurantId();

			if (restaurantId) {
				setRestaurantId(restaurantId); // Set the restaurantId, which triggers another useEffect

				// Will need a fix because doing this using doesn't seem clean
				if (location.pathname === '/commande/adresse') {
					setCart({ ...cart, restaurant_id: restaurantId });
				}

				const cartFromIDB = await getIDBCart(restaurantId);
				if (cartFromIDB) {
					setCart({
						...cartFromIDB, // Set the cart from IndexedDB
						restaurant_id: restaurantId,
					});
				}

				return restaurantId;
			}

			await getDefaultRestaurantId();
		};

		defineDefaultRestaurantId();
	}, []);

	useEffect(() => {
		if (restaurantId && isInitialized) {
			let object = {
				restaurant_id: restaurantId,
			};

			const fetchCart = async () => {
				const cartFromIDB = await getIDBCart(restaurantId);

				let cartFromIDBWithNewCategories = {
					...cartFromIDB,
					categories: cartFromIDB?.categories || cart.categories
				}

				if (cartFromIDB && cartFromIDB.is_no_limit) {
					const newCategories = [...cartFromIDB.categories.map(category => {
						category.is_selected = true;
						category.is_additional = false;

						return category;
					})]

					cartFromIDBWithNewCategories.categories = newCategories;
				}

				return object = {
					...object,
					...cartFromIDBWithNewCategories
				}
			}

			const initializeCart = async () => {
				const newCart = await fetchCart(); // Await the fetchCart call

				setCart(prevState => ({
					...prevState,
					...newCart // Use newObject instead of object
				}));

				await saveCart(newCart.restaurant_id, newCart);
			};

			initializeCart(); // Call the async function
		}
	}, [restaurantId]); // Only trigger when restaurantId changes

	const fetchMenus = async (restaurantIdProp) => {
		if (restaurantId) {
			try {
				
				const response = await axios.get(
					`${import.meta.env.VITE_API_URL}/api/restaurants/${restaurantIdProp || cart?.restaurant_id || restaurantId
					}/menu/list`,
					{
						params: {
							with_product_name: true,
						},
					}
				);

				if (response.status === 200) {
					setCart((prevState) => ({
						...prevState,
						fetched_menus: response.data,
					}));
				}
			} catch (error) {
				if (!cart.restaurant_id && cart.fetched_menus.length === 0 && cart.categories.length === 0) {
					console.error(error);
					toast.error('Une erreur est survenue', {
						position: 'top-center',
						autoClose: 800,
						hideProgressBar: false,
						closeOnClick: true,
						pauseOnHover: false,
						draggable: true,
						theme: 'colored',
					});

					setTimeout(() => (window.location.href = '/commande/adresse'), 1500);
				}

				setCart((prevState) => ({
					...prevState,
					fetched_menus: [],
				}));
			}
		}
	};

	const handleRestaurantChange = async (newRestaurant) => {
		try {
			setPromoApplied(null);
			setpromoId("");

			// Load the cart for the new restaurant from IndexedDB
			const loadedCart = await loadCart(newRestaurant.id);

			if (loadedCart) {
				// Debug pour vérifier l'état du cart chargé
				console.log('OrderContext - loadedCart.is_no_limit:', loadedCart.is_no_limit);
				console.log('OrderContext - loadedCart.is_no_limit_type:', loadedCart.is_no_limit_type);
				console.log('OrderContext - loadedCart.is_no_limit_selected_category:', loadedCart.is_no_limit_selected_category);

				// Restaurant en mode no limit - catégorie spécifique
				if (loadedCart.is_no_limit && loadedCart.is_no_limit_type === 'category' && loadedCart.is_no_limit_selected_category) {
					navigate(`/commande/${loadedCart.is_no_limit_selected_category.name.toLowerCase().split(' ')
						.join('-')
						.replace(',', '-')}`, { state: loadedCart });
				}
				// Restaurant en mode no limit - liste de produits
				else if (loadedCart.is_no_limit && loadedCart.is_no_limit_type === 'list') {
					navigate(`/commande/liste-des-produits`, { state: loadedCart });
				}
				// Restaurant en mode no limit mais configuration incomplète - fallback vers liste
				else if (loadedCart.is_no_limit && (!loadedCart.is_no_limit_type || (loadedCart.is_no_limit_type === 'category' && !loadedCart.is_no_limit_selected_category))) {
					console.warn('OrderContext - Restaurant en mode no limit mais configuration incomplète, redirection vers liste des produits');
					navigate(`/commande/liste-des-produits`, { state: loadedCart });
				}
				// Restaurant normal (avec menus/formules)
				else if (!loadedCart.is_no_limit) {
					navigate('/commande/menu', { state: loadedCart });
				}
				// Fallback - ne devrait jamais arriver
				else {
					console.error('OrderContext - Cas non géré avec loadedCart, redirection vers menu par défaut');
					navigate('/commande/menu', { state: loadedCart });
				}

				// Load previous cart if found in IndexedDB
				setCart({
					...loadedCart,
					...(loadedCart.restaurant_id !== newRestaurant.id ? { restaurant_id: newRestaurant.id } : {}),
					loaded_from_indexeddb: true,
				});
			} else {
				// Make a request to get the new restaurant information
				const newRestaurantResponse = await axios.get(
					`${import.meta.env.VITE_API_URL}/api/restaurants/${newRestaurant.id}`
				);

				let noLimitSelectedCategory;

				if (newRestaurantResponse.status === 200) {
					const newRestaurantFromResponse = newRestaurantResponse.data;

					noLimitSelectedCategory = newRestaurantFromResponse?.is_no_limit_selected_category;
					let obj = {}

					setCart((prevState) => {
						obj = {
							...prevState,
							restaurant_id: newRestaurantFromResponse.id,
							delivery_address: newRestaurantFromResponse?.delivery_address || prevState.delivery_address || cart?.delivery_address,
							is_no_limit: newRestaurantFromResponse?.is_no_limit,
							is_no_limit_type: newRestaurantFromResponse?.is_no_limit_type,
							is_no_limit_minimum_amount: newRestaurantFromResponse?.is_no_limit_minimum_amount,
							is_no_limit_selected_category: newRestaurantFromResponse?.is_no_limit_selected_category
						}

						return obj;
					});

					// Debug pour vérifier l'état du nouveau restaurant
					console.log('OrderContext - newRestaurantFromResponse.is_no_limit:', newRestaurantFromResponse.is_no_limit);
					console.log('OrderContext - newRestaurantFromResponse.is_no_limit_type:', newRestaurantFromResponse.is_no_limit_type);
					console.log('OrderContext - newRestaurantFromResponse.is_no_limit_selected_category:', newRestaurantFromResponse.is_no_limit_selected_category);

					// Restaurant en mode no limit - catégorie spécifique
					if (newRestaurantFromResponse.is_no_limit && newRestaurantFromResponse.is_no_limit_type === 'category' && newRestaurantFromResponse.is_no_limit_selected_category) {
						navigate(`/commande/${newRestaurantFromResponse.is_no_limit_selected_category.name.toLowerCase().split(' ')
							.join('-')
							.replace(',', '-')}`);
					}
					// Restaurant en mode no limit - liste de produits
					else if (newRestaurantFromResponse.is_no_limit && newRestaurantFromResponse.is_no_limit_type === 'list') {
						navigate(`/commande/liste-des-produits`);
					}
					// Restaurant en mode no limit mais configuration incomplète - fallback vers liste
					else if (newRestaurantFromResponse.is_no_limit && (!newRestaurantFromResponse.is_no_limit_type || (newRestaurantFromResponse.is_no_limit_type === 'category' && !newRestaurantFromResponse.is_no_limit_selected_category))) {
						console.warn('OrderContext - Nouveau restaurant en mode no limit mais configuration incomplète, redirection vers liste des produits');
						navigate(`/commande/liste-des-produits`);
					}
					// Restaurant normal (avec menus/formules)
					else if (!newRestaurantFromResponse.is_no_limit) {
						navigate('/commande/menu');
					}
					// Fallback - ne devrait jamais arriver
					else {
						console.error('OrderContext - Cas non géré avec newRestaurantFromResponse, redirection vers menu par défaut');
						navigate('/commande/menu');
					}
				}

				// Fetch initial categories for the new restaurant
				fetchInitialCategories(noLimitSelectedCategory || null, !newRestaurant.is_no_limit ? newRestaurant.id : restaurantId);

				if (cart.restaurant_id !== newRestaurant.id) {
					// Reset cart if no saved cart found for the new restaurant
					setCart((prevState) => ({
						...prevState, fetched_menus: [], menus: [], categories: prevState.categories.map((category) => ({
							...category,
							// selected_products: [], // Clear selected products
							// extra_products: [],    // Clear extra products
							is_done: false
						}))
					}));
				}
			}

			localStorage.removeItem('restaurant_id_ref');
			saveHashedRestaurantId(newRestaurant.id);

			// Update the restaurantId in state
			setRestaurantId(newRestaurant.id);
		} catch (error) {
			console.error('Error loading cart or switching restaurants:', error);
		}
	};

	return (
		<OrdersContext.Provider
			value={{
				setRestaurantId,
				getRestaurantId,
				getDefaultRestaurantId,
				cart,
				setCart,
				setPromoApplied,
				handleApplyPromo,
				handleGetTotalPrice,
				promoId,
				setpromoId,
				saveCart,
				loadCart,
				promoApplied,
				handleCancelPromo,
				handleRestaurantChange,
				fetchMenus,
				fetchInitialCategories,
				isWalletModalOpen,
				setIsWalletModalOpen,
			}}
		>
			{children}
		</OrdersContext.Provider>
	);
};

const useOrdersContext = () => useContext(OrdersContext);

export { OrdersProvider, useOrdersContext, initialCart };